import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    CircularProgress,
    Grid,
    Box,
    Alert,
    Typography,
} from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import IbanInput from "qubCommon/Inputs/IbanInput";
import { Cancel, CheckCircle, Sync } from "@mui/icons-material";
import { customValidateIban, validateIban } from "apis/controllers/externalData";
import { useParams } from "react-router-dom";

const ChangeIban = ({
    open,
    onClose,
    onSubmit,
    initialIban,
    handleChangeIbanSave,
}) => {
    const [checking, setChecking] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [checkInfo, setCheckInfo] = React.useState(null);
    const [validIban, setValidIban] = React.useState(false);
    const { id } = useParams();

    const methods = useForm({});

    const handleCheck = async () => {
        setChecking(true);
        try {
            const result = await customValidateIban(id, methods.getValues().iban);
            setCheckInfo(result?.data);
        } catch (err) {
            console.error("Error during check:", err);
        } finally {
            setChecking(false);
        }
    };

    const handleSubmit = async () => {
        const data = methods.getValues();
        if (submitting) return; // 🚫 prevent multiple concurrent submits

        try {
            setSubmitting(true);
            await handleChangeIbanSave(data.iban);
            onClose();
        } catch (err) {
            console.error("Error saving IBAN:", err);
        } finally {
            setSubmitting(false);
        }
    };

    const renderResult = () => {
        if (!checkInfo) return null;

        const { status, timestamp } = checkInfo;

        return (
            <Box mt={2}>
                {status === "success" ? (
                    <Alert icon={<CheckCircle fontSize="inherit" />} severity="success">
                        {checkInfo?.message}
                    </Alert>
                ) : (
                    <Alert icon={<Cancel fontSize="inherit" />} severity="error">
                        {checkInfo?.message}
                    </Alert>
                )}
                <Typography variant="body2" color="text.secondary" mt={1}>
                    Τελευταίος Έλεγχος: {timestamp}
                </Typography>
            </Box>
        );
    };

    const isBusy = checking || submitting;

    const ibanValue = methods.watch("iban");

    // Reset checkInfo when IBAN changes
    React.useEffect(() => {
        if (checkInfo) {
            setCheckInfo(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ibanValue]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <FormProvider {...methods}>
                <DialogTitle variant="h4">Αλλαγή ΙΒΑΝ</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Stack gap={1}>
                                <Typography>Προηγούμενο IBAN</Typography>
                                <Typography sx={{ textDecoration: 'line-through', fontSize: 16 }} >{initialIban}</Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <IbanInput name="iban" label="Νέο ΙΒΑΝ" disabled={isBusy} setParentValidIban={setValidIban} />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                onClick={handleCheck}
                                variant="contained"
                                startIcon={
                                    checking ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        <Sync />
                                    )
                                }
                                disabled={isBusy || !validIban}
                            >
                                WebService: IBAN/ΑΦΜ
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            {renderResult()}
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={onClose}
                        variant="contained"
                        color="error"
                        disabled={isBusy}
                    >
                        Ακύρωση
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isBusy || checkInfo?.status !== 'success'}
                        onClick={handleSubmit}
                        startIcon={
                            submitting ? <CircularProgress size={20} color="inherit" /> : null
                        }
                    >
                        {submitting ? "Αποθήκευση..." : "Αποθήκευση"}
                    </Button>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
};

export default ChangeIban;
